import { z } from 'zod';

// ============================================================
// SCHEMAS
// ============================================================

/**
 * Erlaubte Status-Werte eines Feature-Requests.
 */
export const FeatureRequestStatusSchema = z.enum(['open', 'in_progress', 'done', 'rejected']);

/**
 * Schema für das Erstellen eines Kommentars zu einem Feature-Request.
 */
export const CreateFeatureRequestCommentSchema = z.object({
  text: z
    .string()
    .min(1, 'Kommentar darf nicht leer sein')
    .max(5000, 'Kommentar zu lang (max. 5000 Zeichen)'),
});

/** Eingabe-Type für POST /feature-requests/:id/comments */
export type CreateFeatureRequestCommentInput = z.infer<typeof CreateFeatureRequestCommentSchema>;

/** Alle erlaubten Status-Werte als Union-Type */
export type FeatureRequestStatus = z.infer<typeof FeatureRequestStatusSchema>;

/**
 * Schema für das Erstellen eines Feature-Requests.
 * Jeder eingeloggte User mit team/admin Rolle kann einen Request erstellen.
 */
export const CreateFeatureRequestSchema = z.object({
  title: z.string().min(3, 'Titel muss mindestens 3 Zeichen lang sein').max(200, 'Titel zu lang'),
  description: z
    .string()
    .min(10, 'Beschreibung muss mindestens 10 Zeichen lang sein')
    .max(2000, 'Beschreibung zu lang'),
});

/** Eingabe-Type für POST /feature-requests */
export type CreateFeatureRequestInput = z.infer<typeof CreateFeatureRequestSchema>;

/**
 * Schema für das Aktualisieren des Status (nur admin).
 */
export const UpdateFeatureRequestStatusSchema = z.object({
  status: FeatureRequestStatusSchema,
});

/** Eingabe-Type für PATCH /feature-requests/:id */
export type UpdateFeatureRequestStatusInput = z.infer<typeof UpdateFeatureRequestStatusSchema>;

/**
 * Vollständiges Feature-Request-Objekt wie es die API zurückgibt.
 */
export const FeatureRequestSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  status: FeatureRequestStatusSchema,
  userId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  user: z
    .object({
      id: z.string(),
      firstName: z.string().nullable(),
      lastName: z.string().nullable(),
      displayName: z.string().nullable(),
      email: z.string(),
    })
    .optional(),
});

/** Feature-Request-Type für Frontend und API */
export type FeatureRequest = z.infer<typeof FeatureRequestSchema>;

/**
 * Liste von Feature-Requests.
 */
export const FeatureRequestListSchema = z.array(FeatureRequestSchema);

/** Type für eine Liste von Feature-Requests */
export type FeatureRequestList = z.infer<typeof FeatureRequestListSchema>;

/**
 * Kommentar zu einem Feature-Request mit Autor-Daten.
 */
export const FeatureRequestCommentSchema = z.object({
  id: z.string(),
  entityType: z.literal('feature_request'),
  entityId: z.string(),
  userId: z.string(),
  text: z.string(),
  createdAt: z.coerce.date(),
  author: z.object({
    id: z.string(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    displayName: z.string().nullable(),
    avatarUrl: z.string().nullable(),
    email: z.string(),
  }),
});

/** Kommentar-Type für Feature-Request */
export type FeatureRequestComment = z.infer<typeof FeatureRequestCommentSchema>;

/**
 * Detail-Ansicht eines Feature-Requests mit allen Kommentaren.
 * Wird von GET /feature-requests/:id zurückgegeben.
 */
export const FeatureRequestDetailSchema = FeatureRequestSchema.extend({
  comments: z.array(FeatureRequestCommentSchema),
});

/** Detail-Type für Feature-Request mit Kommentaren */
export type FeatureRequestDetail = z.infer<typeof FeatureRequestDetailSchema>;
